<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  import { Checkbox, Modal } from 'carbon-components-svelte';

  import type { ExportColumnOption, TableFilter } from './table-types';

  export let open = false,
    columns: ExportColumnOption[] = [],
    filters: TableFilter[] = [],
    initialSelections: Record<string, string[]> = {},
    searchActive = false;

  type CheckableOption = ExportColumnOption & { checked: boolean };
  type Group = { key: string; label: string; kind: 'columns' | 'filter'; options: CheckableOption[] };

  const dispatch = createEventDispatcher<{
    confirm: { columnKeys: string[]; includeSearch: boolean; filterSelections: Record<string, string[]> };
  }>();

  const checkable = (options: ExportColumnOption[]): CheckableOption[] => options.map((option) => ({ ...option, checked: true }));

  const seededFrom = (filter: TableFilter): CheckableOption[] => {
    const selected = initialSelections[filter.key] ?? [];
    return filter.options.map((option) => ({
      key: option.value,
      label: option.text,
      checked: selected.length === 0 || selected.includes(option.value),
    }));
  };

  const buildGroups = (): Group[] => [
    { key: 'columns', label: 'Columns', kind: 'columns', options: checkable(columns) },
    ...filters.map((filter) => ({ key: filter.key, label: filter.label, kind: 'filter' as const, options: seededFrom(filter) })),
  ];

  let groups = buildGroups(),
    includeSearch = true;

  const selectedKeysOf = (group: Group | undefined): string[] =>
    (group?.options ?? []).filter((option) => option.checked).map((option) => option.key);

  const confirm = () => {
    dispatch('confirm', { columnKeys: selectedKeys, includeSearch: searchActive && includeSearch, filterSelections });
    open = false;
  };

  $: selectedKeys = selectedKeysOf(groups.find((group) => group.kind === 'columns'));
  $: filterSelections = Object.fromEntries(
    groups.filter((group) => group.kind === 'filter').map((group) => [group.key, selectedKeysOf(group)])
  );
</script>

<Modal
  bind:open
  modalHeading="Export CSV"
  primaryButtonText="Export"
  secondaryButtonText="Cancel"
  primaryButtonDisabled={!selectedKeys.length}
  on:click:button--secondary={() => (open = false)}
  on:submit={confirm}
>
  {#each groups as group (group.key)}
    <p class="group-label">{group.label}</p>
    <div class="options">
      {#each group.options as option (option.key)}
        <Checkbox bind:checked={option.checked} labelText={option.label} />
      {/each}
    </div>
  {/each}

  {#if searchActive}
    <p class="group-label">Search</p>
    <Checkbox bind:checked={includeSearch} labelText="Apply the current search filter to the export" />
  {/if}
</Modal>

<style>
  .group-label {
    margin: 1rem 0 0.5rem;
    font-weight: 600;
  }

  .options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0 1rem;
  }
</style>
